import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

const ROLES = [
  "admin", "lab_director", "pathologist", "pathologist_assistant",
  "supervisor", "tech", "lab_assistant", "transcriptionist", "viewer",
];
const PERMS = {
  admin: ["cases:write", "cases:void", "cases:delete", "status:write", "signout", "inventory:write", "sendouts:write", "audit:view", "team:manage", "settings:write", "import", "stats:view", "compliance:view", "compliance:edit", "transcription:view", "transcription:write", "assistant"],
  lab_director: ["cases:write", "cases:void", "status:write", "signout", "inventory:write", "sendouts:write", "audit:view", "stats:view", "compliance:view", "compliance:edit", "transcription:view", "assistant"],
  supervisor: ["cases:write", "cases:void", "status:write", "inventory:write", "sendouts:write", "audit:view", "import", "stats:view", "compliance:view", "compliance:edit", "transcription:view", "assistant"],
  pathologist: ["cases:write", "status:write", "signout", "inventory:write", "sendouts:write", "stats:view", "compliance:view", "transcription:view", "transcription:write", "assistant"],
  pathologist_assistant: ["cases:write", "status:write", "inventory:write", "sendouts:write", "compliance:view", "assistant"],
  tech: ["cases:write", "status:write", "inventory:write", "sendouts:write", "assistant"],
  lab_assistant: ["status:write", "inventory:write", "assistant"],
  transcriptionist: ["transcription:view", "transcription:write", "assistant"],
  viewer: [],
};
const ROLE_LABELS = {
  admin: "Administrator", lab_director: "Lab Director", pathologist: "Pathologist",
  pathologist_assistant: "Pathologist Assistant", supervisor: "Supervisor",
  tech: "Technician", lab_assistant: "Lab Assistant",
  transcriptionist: "Transcriptionist", viewer: "Viewer",
};
export { ROLES, ROLE_LABELS };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
      localStorage.removeItem("pathtrax_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("pathtrax_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* noop */
    }
    localStorage.removeItem("pathtrax_token");
    setUser(false);
  };

  const can = (perm) => Boolean(user && (PERMS[user.role] || []).includes(perm));

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, can, reload: load }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
